import config from "appConfig";

const theory = `<h3 class=sectionHead>Electrical line</h3>
<p>
   A line with two parallel conductors can be schematized as shown in <a href="#" data-img-src=${config.ASSETS_URL}/01/28/theory/figure-1.svg data-img-alt="Figure 1. Diagram of an infinitesimal portion of electrical line.">Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/28/theory/figure-1.svg alt="Figure 1. Diagram of an infinitesimal portion of electrical line."><figcaption class=caption>Figure 1. Diagram of an infinitesimal portion of electrical line.</figcaption></figure>
<p>
   In this scheme
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is the inductance per unit length in series and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is the capacitance per unit length. The Ohm law on the length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>x</mi>
   </math>
   allows writing
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>+</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>x</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂i</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   The law of the nodes on the length d
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   allows writing
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>i</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>i</mi>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-bin>+</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>x</mi>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>∂V</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>This leads to the following system called ”telegrapher’s equations”
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂V</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mover accent=true>
                              <mrow>
                                 <mi>L</mi>
                              </mrow>
                              <mo accent=true>¯</mo>
                           </mover>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂i</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂i</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mover accent=true>
                              <mrow>
                                 <mi>C</mi>
                              </mrow>
                              <mo accent=true>¯</mo>
                           </mover>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂V</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   It is noticeable that the two equations are similar: in the second
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   are inverted and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is replaced by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>. Deriving the first equation with respect to time and the second with respect to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>, and invertly leads to the wave equations
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mover accent=true>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mover accent=true>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The solutions of this equation are
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>f</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>ct</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>ct</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>g</mi>
   </math>
   are any functions and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   the speed of the waves, the first traveling to positive
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   direction and the second to negative
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   direction.
<p>
   Integrating the first equation with respect to time leads to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mfrac>
            <mrow>
               <mover>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
                  <mrow>
                     <mo>¯</mo>
                  </mrow>
               </mover>
            </mrow>
            <mrow>
               <mover>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mo>¯</mo>
                  </mrow>
               </mover>
            </mrow>
         </mfrac>
      </msqrt>
   </math>.
<p>In the frequency domain, this leads to the following system:
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂V</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>ω</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mover accent=true>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mo accent=true>¯</mo>
                           </mover>
                           <mi>i</mi>
                           <mo class=MathClass-open>(</mo>
                           <mi>x</mi>
                           <mo class=MathClass-punc>,</mo>
                           <mi>ω</mi>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂i</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>ω</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mi>Ȳ</mi>
                           <mi>V</mi>
                           <mo class=MathClass-open>(</mo>
                           <mi>x</mi>
                           <mo class=MathClass-punc>,</mo>
                           <mi>ω</mi>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mi>ω</mi>
   </math>
   is the series impedance per unit length and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Ȳ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mi>ω</mi>
   </math>
   is the parallel admittance per unit length. The introduction of impedance and admittance allows to include losses writing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mi>ω</mi>
      <mo class=MathClass-bin>+</mo>
      <mover accent=true>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is the series resistance per unit length and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Ȳ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mi>ω</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>Ḡ</mi>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Ḡ</mi>
   </math>
   is the parallel conductance per unit length.
<p>
   Solutions are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>Γx</mi>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Γx</mi>
         </mrow>
      </msup>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Γ</mi>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mover accent=true>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
         </mrow>
      </msqrt>
   </math>
   is the propagation constant. It is related to the wave number by the relation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Γ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jk</mi>
   </math>.
<p>
   For the current
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   is still valid with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
              <mo>/</mo>
            <mover accent=true>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
         </mrow>
      </msqrt>
   </math>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-punc>.</mo>
      <mi>e</mi>
      <mo class=MathClass-punc>.</mo>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>±</mo>
   </math>
   the characteristic impedance of the progressive wave.
<h3 class=sectionHead>Lossless acoustic plane wave</h3>
<p>The linearized equations of acoustics without losses are written
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂p</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mi>γ</mi>
                                 <msub>
                                    <mrow>
                                       <mi>P</mi>
                                    </mrow>
                                    <mrow>
                                       <mi mathvariant=italic>atm</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂p</mi>
                                 <mo class=MathClass-open>(</mo>
                                 <mi>x</mi>
                                 <mo class=MathClass-punc>,</mo>
                                 <mi>t</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   is the acoustic pressure,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>
   the particle velocity,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   the volumic mass of air at rest and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>atm</mi>
         </mrow>
      </msub>
   </math>
   the mean pressure.
<p>
   This equation system is wholly analogous to the previous system by considering the analogy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>p</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>v</mi>
   </math>. We then have the equivalences
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   (this is called acoustic inertance) and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>γ</mi>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>atm</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   (this is called acoustic compliance). We then deduce
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>γ</mi>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>atm</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   the speed of sound. We also have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mi>γ</mi>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>atm</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
   </math>.
<p>
   In a waveguide the analogy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>U</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>vS</mi>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is the cross section of the pipe is usually prefered. We then have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>γ</mi>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>atm</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msqrt>
               <mrow>
                  <mi>γ</mi>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>atm</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msqrt>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </mfrac>
   </math>. The speed of sound is obviously the same.
<h3 class=sectionHead>Compression waves in a bar</h3>
<p>
   Considering a uniform bar with a cross section
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>, density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and a young modulus
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
   </math>
   we can write
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂F</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mi>S</mi>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂v</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>ES</mi>
                              </mrow>
                           </mfrac>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂F</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
   </math>
   is the force and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
   </math>
   the particle velocity.
<p>
   Considering the analogy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>F</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>v</mi>
   </math>, we have the equivalences
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>S</mi>
   </math>
   the inertance and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ES</mi>
         </mrow>
      </mfrac>
   </math>
   the compliance. We then deduce
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>E</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   the speed of waves. We also have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <mi>S</mi>
      <msqrt>
         <mrow>
            <mi>E</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi mathvariant=italic>cS</mi>
   </math>.
<h3 class=sectionHead>Torsional waves in a bar</h3>
<p>
   The rotational inertia applied to an infinitesimal length leads to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂x</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mi>ρ</mi>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mi>∂</mi>
            <msup>
               <mrow>
                  <mi>t</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   torque,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   the density,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   the polar second momentum of the cross section and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   the torsion angle of the cross section. The theory of beams under torsion also allows writing:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂𝜃</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂t</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi>G</mi>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>G</mi>
   </math>
   the Coulomb modulus. Introducing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>∂𝜃</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂t</mi>
         </mrow>
      </mfrac>
   </math>
   this leads to
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>M</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>I</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂Ω</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mfrac>
                              <mrow>
                                 <mi mathvariant=italic>∂Ω</mi>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mi>G</mi>
                                 <msub>
                                    <mrow>
                                       <mi>I</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>0</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>M</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Considering the analogy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <mi>Ω</mi>
   </math>, we have the equivalences
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mi>ρ</mi>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   the inertance and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>G</mi>
            <msub>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   the compliance. We then deduce
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   the speed of waves. We also have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msqrt>
         <mrow>
            <mi>G</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>c</mi>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>.
<h3 class=sectionHead>Vibrating string</h3>
<p>
   Consider a uniform string with a linear mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
   </math>
   under a tension
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/28/theory/figure-2.svg data-img-alt="Figure 2. Diagram of an infinitesimal portion of string.">Figure 2</a>). Considering an infinitesimal portion of string of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>x</mi>
   </math>, this is submitted to a force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <mover accent=true>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   on one side and a force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   on the other.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/01/28/theory/figure-2.svg alt="Figure 2. Diagram of an infinitesimal portion of string."><figcaption class=caption>Figure 2. Diagram of an infinitesimal portion of string.</figcaption></figure>
<p>
   The fundamental relation of dynamics applied to this portion of string is written: 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>−</mo>
      <mover accent=true>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>μ</mi>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>x</mi>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <mover accent=true>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mo accent=true>→</mo>
            </mover>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂t</mi>
         </mrow>
      </mfrac>
   </math>. The projection along y of the fundamental equation of dynamics thus leads to
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂x</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>μ</mi>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
                  <msub>
                     <mrow>
                        <mi>v</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂t</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Furthermore,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
   </math>
   decomposes into
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>T</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>≈</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>T</mi>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>≈</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. Now
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂x</mi>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>u</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   is the transverse displacement. So
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂x</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>≈</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Finally, after derivation of the second equation and considering that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>∂</mi>
            <msub>
               <mrow>
                  <mi>u</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi mathvariant=italic>∂t</mi>
         </mrow>
      </mfrac>
   </math>, the following system is obtained
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable equalrows=false style="" align=axis equalcolumns=false columnlines=none class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>y</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mi>μ</mi>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>v</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>y</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>v</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>y</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂x</mi>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>0</mn>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                           <mfrac>
                              <mrow>
                                 <mi>∂</mi>
                                 <msub>
                                    <mrow>
                                       <mi>T</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>y</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <mi mathvariant=italic>∂t</mi>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Considering the analogy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>, we have the equivalences
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mi>μ</mi>
   </math>
   the inertance and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-rel>⇔</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   the compliance. So the tension
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   gives the stiffness to the string. We then deduce
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   the speed of waves. We also have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mi>μ</mi>
            <msub>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>μc</mi>
   </math>
   the characteristic impedance.
<h3 class=sectionHead>Summary of the analogies</h3>
<p>Analogies are summarized in the <a href=# data-img-src=${config.ASSETS_URL}/01/28/theory/table-1.svg data-img-alt="Table 1. Summary of analogies.">Table 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Summary of analogies.</figcaption>
      <img src=${config.ASSETS_URL}/01/28/theory/table-1.svg alt="Table 1. Summary of analogies.">
   </figure>
</div>
<p>
   Notice that, as the two variables play the same role, analogies can be inverted. For example for the string it can be considered
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>⇔</mo>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>. This is called admittance analogy while the other is the impedance analogy. In mechanics the mobility (i.e. the mechanical admittance) is often considered because more relevant. For example when writing the analogy between violin and saxophone the relevant analogy is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>⇔</mo>
      <mi>p</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>⇔</mo>
      <mi>v</mi>
   </math>.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
   <dt>[2]
   <dd>L. Brillouin. Wave propagation in periodic structures. MC Graw Hill, 1946.
   <dt>[3]
   <dd>A. Chaigne and J. Kergomard. Acoustics of Musical Instruments. Springer, New York, 2016.
   <dt>[4]
   <dd>M. L. Munjal. Acoustics of Ducts and Mufflers. Wiley, New York, 1987.
   <dt>[5]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[6]
   <dd>M. Rossi.  ́Electroacoustique. Presses Polytechniques Romandes, Lausanne, 1986.
   <dt>[7]
   <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
   <dt>[8]
   <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
